/**
 * A WHITELIST renderer for the `show_info` card body — the markdown subset and
 * nothing else, built as React elements and never as HTML.
 *
 * THE SECURITY STANCE, because it is the reason this file exists instead of a
 * dependency. The body is MODEL-AUTHORED text arriving over the control socket,
 * and one of the two places that render it is a widget inside a closed shadow
 * root on somebody else's website. So:
 *
 *   - NO `dangerouslySetInnerHTML`, ever, and no HTML parsing of any kind. An
 *     angle bracket in the text is a character, not a tag — React escapes it the
 *     way it escapes every string child, so `<script>` renders as the six
 *     characters it is.
 *   - LINKS ARE https-ONLY. `javascript:`, `data:`, `vbscript:`, plain `http:`,
 *     a protocol-relative `//…` — all of it renders as the literal text the
 *     model typed, because a card that can put a click-me on a customer's page
 *     must not be able to point it anywhere but a real https URL. What does pass
 *     opens in a new tab with `rel="noopener noreferrer"`, so the target page
 *     can neither script this one nor learn where it came from.
 *   - IMAGES ARE https-ONLY TOO (added 2026-09-01), and an image is the one
 *     construct here that makes a request on the reader's behalf: an
 *     `![alt](https://…)` becomes an <img> with `referrerPolicy="no-referrer"`,
 *     so the image host learns nothing about the page it was shown on, and
 *     `loading="lazy"`. Anything but an absolute https URL renders as the
 *     literal text the model typed. Where the url may come FROM is the tool
 *     description's job (tools.ts): a tool result — a knowledge article's step
 *     screenshot — never imagination.
 *   - EVERYTHING UNRECOGNISED IS LITERAL. An unmatched `**`, a stray `*`, a
 *     half-written `[link](…` — rendered as typed. A whitelist renderer's
 *     failure mode must be "looks unformatted", never "did something".
 *
 * The grammar, in full: paragraphs split on blank lines, and a single newline
 * inside a paragraph is a LINE BREAK (the chat reading, not markdown's
 * join-with-a-space — see the note at the render step); lines starting `- ` or
 * `* ` are bullets and `1. ` (any digits) are numbered items, consecutive list
 * lines grouping into one list; inline `**bold**`, `*italic*`,
 * `[text](https://…)` and `![alt](https://…)`. Nothing nests — link text, alt
 * text and emphasis contain plain text only — and that flatness is a decision,
 * not a gap: every layer of nesting is another parser state to get wrong.
 *
 * Styling is INLINE `CSSProperties` supplied by the caller, because one caller
 * is the stylesheet-free chat widget (px-only inline styles, see chat-flag.tsx)
 * and the other is a Tailwind page. The defaults below keep `{}` honest in both:
 * they carry the list markers and indent explicitly, so Tailwind's preflight
 * (`ul { list-style: none; padding: 0 }`) cannot strip the bullets.
 */
import { Fragment } from 'react'
import type { CSSProperties, ReactNode } from 'react'

export interface MiniMarkdownStyles {
  paragraph?: CSSProperties
  /** Applied to both <ul> and <ol>; the marker type is a per-tag default. */
  list?: CSSProperties
  listItem?: CSSProperties
  link?: CSSProperties
  strong?: CSSProperties
  em?: CSSProperties
  /** Merged over the image defaults (block, full width, 8 px radius). */
  image?: CSSProperties
  /** The button an image becomes when `onImageClick` is given (see below). */
  imageButton?: CSSProperties
}

/** One image the reader clicked: what to show large, and what to give focus
 *  back to when the large view closes. */
export interface MiniMarkdownImage {
  src: string
  alt: string
  /** The element that was activated — the caller restores focus to it. */
  trigger: HTMLElement | null
}

export interface MiniMarkdownOptions {
  /**
   * Given, every image renders inside a real `<button>` that calls this — the
   * chat flag opens a lightbox (2026-09-01). A button rather than a click
   * handler on the picture, so the enlargement is reachable by keyboard and
   * has a name. Absent, images are plain pictures; the control panel's
   * transcript passes nothing and is unchanged. The url reaching this callback
   * has ALREADY passed the https whitelist — it is the src the card rendered.
   */
  onImageClick?: (image: MiniMarkdownImage) => void
}

/** Blocks after the first get this gap, so `{}` still separates paragraphs. */
const BLOCK_GAP = 8
/** The indent that makes list markers visible; callers can override via `list`. */
const LIST_INDENT = 18

/** Bold first so `**x**` is never read as two italics; emphasis content may
 *  not START OR END with whitespace, so a stray `**` cannot re-pair with a
 *  later lone `*` across half a sentence (`a ** b * c` stays literal); the
 *  image BEFORE the link, because a scan that reached the `[` of `![alt](…)`
 *  first would read it as a link and leave a stray `!` — matches are found
 *  left to right, so the `!` alternative wins at its own index; the href and
 *  src stop at the first `)` or whitespace, so a malformed URL cannot swallow
 *  the line. Alt text may be empty (`![](…)`); link text may not. */
const INLINE =
  /\*\*(\S(?:[^*]*\S)?)\*\*|\*(\S(?:[^*]*\S)?)\*|!\[([^\]]*)\]\(([^()\s]+)\)|\[([^\]]+)\]\(([^()\s]+)\)/g

/** True only for a parseable, absolute https URL — the whole whitelist. */
function isHttps(href: string): boolean {
  try {
    return new URL(href).protocol === 'https:'
  } catch {
    return false
  }
}

/** One line of text into inline runs. Anything the regex does not claim — and
 *  any link that fails the https check — stays a plain string child. */
function inline(text: string, styles: MiniMarkdownStyles, options: MiniMarkdownOptions): ReactNode[] {
  const out: ReactNode[] = []
  let last = 0
  INLINE.lastIndex = 0
  for (let m = INLINE.exec(text); m !== null; m = INLINE.exec(text)) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const [whole, bold, italic, imgAlt, imgSrc, linkText, href] = m
    if (bold !== undefined) {
      out.push(<strong key={m.index} style={styles.strong}>{bold}</strong>)
    } else if (italic !== undefined) {
      out.push(<em key={m.index} style={styles.em}>{italic}</em>)
    } else if (imgSrc !== undefined && isHttps(imgSrc)) {
      // Block-level inside the paragraph, full width and never wider: a card is
      // a glance at ~340 px, and a screenshot that overflowed it would scroll
      // the whole panel sideways. No referrer, lazy — see the header.
      const alt = imgAlt ?? ''
      const img = (
        <img
          key={m.index}
          src={imgSrc}
          alt={alt}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          style={{ display: 'block', maxWidth: '100%', height: 'auto', borderRadius: 8, margin: '6px 0', ...styles.image }}
        />
      )
      const onImageClick = options.onImageClick
      if (onImageClick === undefined) {
        out.push(img)
      } else {
        // A button around the picture, not a handler on it: reachable by Tab,
        // named for a screen reader, and the same size as the picture so
        // nothing else in the card becomes a target. The UA's own focus ring
        // is left in place — there is no stylesheet here to draw a better one.
        out.push(
          <button
            key={m.index}
            type="button"
            onClick={(e) => onImageClick({ src: imgSrc, alt, trigger: e.currentTarget })}
            aria-label={alt !== '' ? `Enlarge image: ${alt}` : 'Enlarge image'}
            title="Click to enlarge"
            style={{
              display: 'block',
              width: '100%',
              boxSizing: 'border-box',
              appearance: 'none',
              border: 0,
              margin: 0,
              padding: 0,
              background: 'transparent',
              color: 'inherit',
              font: 'inherit',
              textAlign: 'left',
              cursor: 'zoom-in',
              ...styles.imageButton,
            }}
          >
            {img}
          </button>,
        )
      }
    } else if (linkText !== undefined && href !== undefined && isHttps(href)) {
      out.push(
        <a
          key={m.index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'inherit', textDecoration: 'underline', ...styles.link }}
        >
          {linkText}
        </a>,
      )
    } else {
      // A link or image that is not https renders as the literal characters, so
      // the reader sees exactly what the model tried and nothing is fetched or
      // clickable.
      out.push(whole)
    }
    last = m.index + whole.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

/** A parsed block: one paragraph's lines, or one list's item lines. */
type Block =
  | { kind: 'p'; lines: string[] }
  | { kind: 'ul' | 'ol'; items: string[] }

/**
 * Render the markdown subset to React elements.
 *
 * `styles` may be `{}`: the defaults carry spacing between blocks, the list
 * indent and the marker types, so an unstyled call still reads as formatted
 * text rather than as a column of flush-left lines.
 */
export function renderMiniMarkdown(
  text: string,
  styles: MiniMarkdownStyles = {},
  options: MiniMarkdownOptions = {},
): ReactNode {
  // --- lines into blocks. A blank line ends whatever is open; a change of
  // shape (paragraph -> bullet, bullet -> numbered) also ends it, so a list
  // pressed against a paragraph with no blank line between still groups right.
  const blocks: Block[] = []
  let open: Block | null = null
  for (const raw of text.split('\n')) {
    const line = raw.trim()
    if (line === '') {
      open = null
      continue
    }
    const bullet = /^[-*] (.*)$/.exec(line)
    const numbered = /^\d+\. (.*)$/.exec(line)
    const kind = bullet ? 'ul' : numbered ? 'ol' : 'p'
    if (open === null || open.kind !== kind) {
      open = kind === 'p' ? { kind, lines: [] } : { kind, items: [] }
      blocks.push(open)
    }
    if (open.kind === 'p') open.lines.push(line)
    else open.items.push((bullet ?? numbered)![1]!)
  }

  // --- blocks into elements. Consecutive paragraph lines keep their line
  // breaks (<br>, the chat-app reading of a single newline), NOT markdown's
  // join-with-a-space: the author here is a voice model, and when it forgets
  // the "- " bullets on a list — session 59ef61e4, five incidents run together
  // into one wall of text — each line was still a deliberate item. A model
  // does not hard-wrap prose mid-sentence, so the case soft breaks exist for
  // never arrives, and honouring the newline turns a forgotten bullet into a
  // readable line instead of a formatting accident.
  return blocks.map((b, i) => {
    const gap: CSSProperties = i === 0 ? {} : { marginTop: BLOCK_GAP }
    if (b.kind === 'p') {
      return (
        <p key={i} style={{ margin: 0, ...gap, ...styles.paragraph }}>
          {b.lines.map((line, j) => (
            <Fragment key={j}>
              {j > 0 && <br />}
              {inline(line, styles, options)}
            </Fragment>
          ))}
        </p>
      )
    }
    const List = b.kind
    return (
      <List
        key={i}
        style={{
          margin: 0,
          ...gap,
          paddingLeft: LIST_INDENT,
          // Stated, not inherited: Tailwind's preflight strips list markers,
          // and the shadow-root widget has no stylesheet to restore them.
          listStyleType: b.kind === 'ul' ? 'disc' : 'decimal',
          ...styles.list,
        }}
      >
        {b.items.map((item, j) => (
          <li key={j} style={styles.listItem}>
            {inline(item, styles, options)}
          </li>
        ))}
      </List>
    )
  })
}
